/**
 * InstallAppBanner — appears on mobile web sessions.
 *
 * Two modes:
 *   1. Deep-link mode (path is a shareable URL like /team/abc123):
 *      offers a one-tap install with **deferred deep linking** —
 *      mints a token on the server, copies it to clipboard, redirects
 *      to the App Store / Play Store. After install + first launch
 *      the mobile app reads the clipboard, calls
 *      `/api/install/claim/{token}`, and routes to the original path.
 *
 *   2. General mode (path is null or a tab root):
 *      lighter copy ("Get the app"), no deep-link mint. Tap goes
 *      straight to the App Store / Play Store. Dismissal honoured
 *      via localStorage for 24h so users on the tab roots aren't
 *      pestered every session.
 *
 * Apple's Smart App Banner (configured in `index.html`) is the iOS-native
 * version of this and "just works" once the app is in the App Store. This
 * component is the cross-platform fallback that ALSO covers Android +
 * users who dismiss the Smart Banner.
 */
import { useState } from 'react';
import { Download, X, Smartphone } from 'lucide-react';
import api from '../lib/api';

// Replace these once the apps are live in the stores.
const APP_STORE_URL  = 'https://apps.apple.com/app/idREPLACE_WITH_APP_STORE_ID';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.universalfantasy.league';

// 24-hour dismissal cooldown for the general (non-deep-link) banner.
// Stored in localStorage so it persists across tabs and reloads but
// reappears tomorrow.
const DISMISS_COOLDOWN_MS = 24 * 60 * 60 * 1000;
const DISMISS_KEY_GENERAL = 'fws_install_dismissed_until';
const DISMISS_KEY_DEEPLINK = 'fws_install_dismissed';  // session-scoped, legacy

function _isMobileBrowser() {
  if (typeof navigator === 'undefined') return false;
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

function _platform() {
  if (/Android/i.test(navigator.userAgent)) return 'android';
  if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) return 'ios';
  return 'desktop';
}

// True when the user has dismissed within the last 24 hours.
function _dismissedRecently() {
  try {
    const until = parseInt(localStorage.getItem(DISMISS_KEY_GENERAL) || '0', 10);
    return Date.now() < until;
  } catch { return false; }
}

export default function InstallAppBanner({ path }) {
  const isDeepLink = !!path;

  const [dismissed, setDismissed] = useState(() => {
    // Deep-link banners dismiss until tab close. General banners
    // dismiss for 24h. Either kind respects the 24h cooldown
    // (so dismissing on /stage also hides the deep-link banner
    // until tomorrow — fair, the user told us they're not interested).
    try {
      if (_dismissedRecently()) return true;
      if (isDeepLink && sessionStorage.getItem(DISMISS_KEY_DEEPLINK) === '1') return true;
      return false;
    } catch { return false; }
  });
  const [busy, setBusy] = useState(false);

  if (dismissed) return null;
  if (!_isMobileBrowser()) return null;     // desktop users skip the banner

  const dismiss = () => {
    setDismissed(true);
    try {
      // 24h cooldown always — applies to both modes. The session-scoped
      // key is kept around for backwards compatibility with v5.8.x.
      localStorage.setItem(
        DISMISS_KEY_GENERAL,
        String(Date.now() + DISMISS_COOLDOWN_MS),
      );
      if (isDeepLink) sessionStorage.setItem(DISMISS_KEY_DEEPLINK, '1');
    } catch {
      /* localStorage blocked — harmless, banner just reappears */
    }
  };

  const handleInstall = async () => {
    setBusy(true);
    try {
      const platform = _platform();
      const storeUrl = platform === 'android' ? PLAY_STORE_URL : APP_STORE_URL;

      if (isDeepLink) {
        // Deep-link mode: mint a token, copy it, try custom scheme, then fall back to store.
        try {
          const { data } = await api.post('/install/token', { path });
          const token = data?.token;
          if (token) {
            try {
              await navigator.clipboard.writeText(`fws-install:${token}`);
            } catch {
              // Clipboard write blocked — fine, Smart Banner / Universal
              // Links typically handle the deferred case on iOS anyway.
            }
            // Try the custom scheme — opens the app immediately if installed
            const customScheme = `universalfantasy:/${path.startsWith('/') ? path : `/${path}`}`;
            window.location.href = customScheme;
            // Fallback after a tick — go to the store
            setTimeout(() => { window.location.href = storeUrl; }, 600);
            return;
          }
        } catch {
          // Token mint failed — fall through to plain store redirect.
        }
      }

      // General mode (or deep-link token failed): straight to the store.
      window.location.href = storeUrl;
    } finally {
      setBusy(false);
    }
  };

  // v5.8.21 — Copy adapts to mode. Deep-link mode promises the deferred
  // bounce-back ("we'll bring you right back here"); general mode just
  // sells the app on its own merits.
  const headline = isDeepLink
    ? 'Get the Fantasy World Star app'
    : 'Fantasy World Star on iOS & Android';
  const subhead = isDeepLink
    ? "Tap install — we'll bring you right back here."
    : (_platform() === 'android' ? 'Available on Google Play.' : 'Available on the App Store.');

  return (
    <div
      className="fixed bottom-0 inset-x-0 z-[80] mx-auto max-w-md p-3 pb-[calc(env(safe-area-inset-bottom,0px)+12px)]"
      data-testid="install-app-banner"
      data-mode={isDeepLink ? 'deep-link' : 'general'}
    >
      <div className="rounded-2xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] shadow-2xl border border-[hsl(var(--accent-foreground)/0.2)] flex items-center gap-3 px-4 py-3">
        <Smartphone className="h-5 w-5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold leading-tight">{headline}</p>
          <p className="text-[11px] opacity-80 leading-tight">{subhead}</p>
        </div>
        <button
          onClick={handleInstall}
          disabled={busy}
          className="bg-[hsl(var(--accent-foreground))] text-[hsl(var(--accent))] text-xs font-bold rounded-full px-3 py-1.5 disabled:opacity-50"
          data-testid="install-app-cta"
        >
          {busy ? '…' : (<><Download className="h-3.5 w-3.5 inline mr-1" /> {isDeepLink ? 'Install' : 'Get App'}</>)}
        </button>
        <button
          onClick={dismiss}
          className="opacity-70 hover:opacity-100"
          aria-label="Dismiss"
          data-testid="install-app-dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
